<template>
  <div>
    <div>
      <AvailableBalance
        class="sidebar__available-balance"
        :available-balance-data="unifiedAccountAvailableBalance"
        :error="hasBalancesBrokerRequestFailed"
        :loading="isBalancesBrokerRequestLoading"
        :has-margin-allocation="hasMarginAllocation"
        @on-retry="fetchBalances"
      />

      <TradeStatus
        v-if="isMarketNotAvailable"
        :status="statusMarketAvailable"
      />

      <PositionsSummarySection />

      <ServicesSection
        v-if="serviceButtons.length"
        :service-buttons="serviceButtons"
        :is-loading-error="isServicesLoading"
        @open-home-broker="onAccessHomeBrokerClick"
        @open-copytrade="onAccessCopytradeClick"
        @retry-services="getTradeButtons(true)"
      />

      <WatchList
        :set-watchlist-subscription="getWatchlistSubscription"
        :set-panel="() => setWatchlistPanel(setPanel)"
      />
    </div>

    <GeneralFooter @help-click="handleHelpClick" />

    <FeeTransparencyDisclaimer />
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';

import { ServicesSection } from '../ServicesSection';
import { WatchList } from '../WatchList';
import { TradeStatus } from './components/TradeStatus';
import { SidebarTypes } from '@/modules/trade/views/TradeView/components/TradeSidebar';
import { GeneralFooter } from './components/GeneralFooter';
import { PositionsSummarySection } from './components/PositionsSummarySection';
import FeeTransparencyDisclaimer from '@/modules/trade/views/TradeView/components/FeeTransparencyDisclaimer/FeeTransparencyDisclaimer.vue';
import { isImpersonate } from '@/modules/common/services/partner';
import { RouteMixin } from '@/modules/common/mixins/route';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import { AccountStatus, IHeroBalances } from '@/modules/trade/services/account';
import {
  trackHomeBrokerClick,
  trackHelpClick,
  trackStatementAndNotesClick
} from '@/modules/trade/views/TradeView/trackers';
import { MFAModules } from '@/modules/authentication/views/SecurityView/components/MultiFactorAuthentication';
import { RequestStatus } from '@/modules/trade/store/trade';
import { AvailableBalance } from '@/modules/trade/components/AvailableBalance';
import {
  getTradeCommonButtons,
  getTradeCopyButtons,
  getTradeHomeBrokerButtons,
  getTradeMarginAllocationButtons,
  getTradeRLPButtons,
  getTradeSecuritiesLendingButtons,
  TradeButton
} from '@/modules/trade/services/buttons';
import { useDashStore } from '@/modules/common/store/DashModule';

const tradePositionsModule = namespace('tradeModule');

const TRADE_FUNCTIONS_MAP = {
  common: getTradeCommonButtons,
  lending: getTradeSecuritiesLendingButtons,
  margin: getTradeMarginAllocationButtons,
  rlp: getTradeRLPButtons,
  hb: getTradeHomeBrokerButtons,
  copy: getTradeCopyButtons
};

// No GeneralSidebarList.vue, logo abaixo dos imports
interface TradeButtonExtended extends TradeButton {
  serviceKey?: string;
  hasError?: boolean;
}

@Component({
  components: {
    ServicesSection,
    WatchList,
    TradeStatus,
    GeneralFooter,
    PositionsSummarySection,
    AvailableBalance,
    FeeTransparencyDisclaimer
  }
})
export default class GeneralSidebarList extends mixins(RouteMixin) {
  @tradePositionsModule.State('userHasBrokerAccount')
  readonly userHasBrokerAccount!: boolean;

  @tradePositionsModule.State('balancesBrokerRequestStatus')
  readonly balancesBrokerRequestStatus!: RequestStatus;

  @tradePositionsModule.Action('fetchBalances')
  readonly fetchBalances!: Function;

  @Prop({ type: Object, required: false })
  readonly balances?: IHeroBalances;

  @Prop({ type: Object, required: false })
  readonly tradeStatus?: AccountStatus;

  @Prop({ type: Function, default: () => {} })
  readonly onAccessHomeBrokerClick!: Function;

  @Prop({ type: Function, default: () => {} })
  readonly onAccessCopytradeClick!: Function;

  @Prop({ type: Function, required: true })
  readonly setPanel!: Function;

  @Prop({ type: Function, required: true })
  readonly getWatchlistSubscription!: () => {};

  @Prop({ type: Function, default: () => {} })
  readonly openBrokerModal!: Function;

  private isImpersonate = isImpersonate();

  private serviceButtons: TradeButtonExtended[] = [];
  private isServicesLoading = false; // Controle do loading do ErrorHandler
  private failedServiceKeys: string[] = [];

  readonly dashStore = useDashStore();

  private get homeBrokerButtonEnable() {
    if (this.isImpersonate || this.updatedAccountRegulation) {
      return false;
    }

    return true;
  }

  get unifiedAccountAvailableBalance() {
    return {
      availableOnAccountValue: this.balances?.account?.availableBalance,
      availableToOperateValue: this.balances?.account?.detail?.availableBalance,
      toSettleValue: this.balances?.account?.detail?.liquidatedBalance
    };
  }

  get hasBalancesBrokerRequestFailed() {
    return this.balancesBrokerRequestStatus === RequestStatus.Error;
  }

  get isBalancesBrokerRequestLoading() {
    return this.balancesBrokerRequestStatus === RequestStatus.Loading;
  }

  get isMarketNotAvailable() {
    return !this.tradeStatus?.marketAvailable?.isMarketAvailable;
  }

  get statusMarketAvailable() {
    return this.tradeStatus?.marketAvailable;
  }

  get isDarkMode() {
    return this.dashStore.isDarkMode;
  }

  /**
   * This is temporally, this panel transition will be change for a new all positions page
   */
  public setSubscriptionsPanel(setPanel: (param: SidebarTypes) => void) {
    setPanel(SidebarTypes.SubscriptionsList);
    window.scrollTo(0, 0);
  }

  /**
   * This is temporally, this panel transition will be change for a new all watchlist page
   */
  public setWatchlistPanel(setPanel: (param: SidebarTypes) => void) {
    setPanel(SidebarTypes.Watchlist);
    window.scrollTo(0, 0);
  }

  public goToSecurity() {
    trackHomeBrokerClick(
      'Ativar Home Broker - Cadastre a verificação em duas etapas'
    );
    this.$router.push({
      name: 'security',
      query: { module: MFAModules.TRADE }
    });
  }

  handleHelpClick() {
    trackHelpClick();
  }

  public trackAccountStatements() {
    trackStatementAndNotesClick();
  }

  private get updatedAccountRegulation() {
    return this.tradeStatus && !this.tradeStatus.enabledBuy;
  }

  private setSummaryPanels(panel: SidebarTypes) {
    this.setPanel(panel);
    window.scrollTo(0, 0);
  }

  // Adicione este tipo para organizar o estado
  private serviceData: Record<
    string,
    { buttons: TradeButtonExtended[]; hasError: boolean }
  > = {};

  private async getTradeButtons(isRetry = false) {
    this.isServicesLoading = true;

    const allKeys = Object.keys(TRADE_FUNCTIONS_MAP);

    // Define exatamente o que precisa ser buscado
    const keysToFetch = isRetry
      ? allKeys.filter(
          key => this.serviceData[key]?.hasError || !this.serviceData[key]
        )
      : allKeys;

    if (keysToFetch.length === 0) {
      this.isServicesLoading = false;
      return;
    }

    const results = await Promise.allSettled(
      keysToFetch.map(key =>
        TRADE_FUNCTIONS_MAP[key as keyof typeof TRADE_FUNCTIONS_MAP]()
      )
    );

    // 1. Atualiza o dicionário de dados (serviceData)
    keysToFetch.forEach((key, index) => {
      const result = results[index];

      if (result.status === 'fulfilled') {
        this.serviceData[key] = {
          buttons: (result.value as TradeButton[]).map(btn => ({
            ...btn,
            serviceKey: key
          })),
          hasError: false
        };
      } else {
        this.serviceData[key] = {
          buttons: [this.createErrorButton(key)],
          hasError: true
        };
      }
    });

    // 2. Reconstrói o array serviceButtons baseado na ordem fixa de allKeys
    this.serviceButtons = allKeys.flatMap(
      key => this.serviceData[key]?.buttons || []
    );

    // 3. Atualiza a lista de falhas para o ErrorHandler e para o próximo retry
    this.failedServiceKeys = allKeys.filter(
      key => this.serviceData[key]?.hasError
    );

    this.isServicesLoading = false;
  }

  private createErrorButton(key: string): TradeButtonExtended {
    return {
      icon: 'EF0061',
      label: 'Erro ao carregar',
      name: 'error',
      enabled: false,
      serviceKey: key
    };
  }

  private get hasMarginAllocation() {
    if (!this.serviceButtons) return false;
    return this.serviceButtons.some(
      button => button.name === 'marginAllocation' && button.enabled
    );
  }

  async mounted() {
    await this.getTradeButtons();
  }
}
</script>
<style lang="less" scoped>
.sidebar {
  &__available-balance {
    margin-bottom: @size-spacing-x800;
    width: 100%;
  }
}
</style>
